import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { fmtGBP, fmtMoney, fmtDate, statusTone, HOUSES, CITIES, CATEGORIES } from "@/lib/format";
import LotDrawer from "@/components/LotDrawer";
import { toast } from "sonner";
import {
  Search, SlidersHorizontal, Download, ArrowUpDown, ChevronLeft, ChevronRight,
  ExternalLink, RotateCcw,
} from "lucide-react";

const EMPTY = {
  q: "", artist: "", houses: [], city: "", start_date: "", end_date: "",
  status: "", category: "", currency: "", price_basis: "",
  gbp_min: "", gbp_max: "", review_required: "", has_bid: "",
};

const COLS = [
  { key: "artist", label: "Artist" },
  { key: "title", label: "Title / Year" },
  { key: "auction_house", label: "House" },
  { key: "sale_location", label: "Location" },
  { key: "sale_date", label: "Sale Date" },
  { key: "lot_number", label: "Lot" },
  { key: "status", label: "Status" },
  { key: "estimate", label: "Estimate" },
  { key: "result_price", label: "Result (native)" },
  { key: "result_price_basis", label: "Basis" },
  { key: "result_price_gbp", label: "GBP" },
  { key: "highest_bid_if_unsold", label: "High Bid" },
  { key: "source", label: "Source" },
];

export default function Dashboard() {
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "sale_date", dir: "desc" });
  const [drawer, setDrawer] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const params = () => {
    const p = { page, limit: 25, sort_by: sort.by, sort_dir: sort.dir };
    Object.entries(applied).forEach(([k, v]) => {
      if (v === "" || (Array.isArray(v) && v.length === 0)) return;
      p[k] = Array.isArray(v) ? v.join(",") : v;
    });
    return p;
  };

  const { data, isFetching } = useQuery({
    queryKey: ["lots", applied, page, sort],
    queryFn: async () => (await api.get("/lots", { params: params() })).data,
    placeholderData: keepPreviousData,
  });

  const apply = () => { setApplied(filters); setPage(1); };
  const reset = () => { setFilters(EMPTY); setApplied(EMPTY); setPage(1); };
  const toggleHouse = (h) =>
    setFilters((f) => ({ ...f, houses: f.houses.includes(h) ? f.houses.filter((x) => x !== h) : [...f.houses, h] }));
  const setSortKey = (k) =>
    setSort((s) => ({ by: k, dir: s.by === k && s.dir === "desc" ? "asc" : "desc" }));

  const exportUrl = (profile, fmt) => {
    const sp = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => {
      if (v === "" || (Array.isArray(v) && v.length === 0)) return;
      sp.set(k, Array.isArray(v) ? v.join(",") : v);
    });
    sp.set("profile", profile); sp.set("fmt", fmt);
    sp.set("sort_by", sort.by); sp.set("sort_dir", sort.dir);
    return `${API}/export?${sp.toString()}`;
  };
  const doExport = async (profile, fmt) => {
    try {
      const res = await api.get(exportUrl(profile, fmt).replace(API, ""), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `auctionscope_${profile}.${fmt === "excel" ? "xlsx" : "csv"}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${profile} (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Not authorized for this export profile" : "Export failed");
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / 25));

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Combined Auction Database</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Research Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="toggle-filters-button" onClick={() => setShowFilters((v) => !v)} className="al-btn-ghost lg:hidden">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <div className="flex items-center gap-1 al-card p-1">
            <button data-testid="export-csv-button" onClick={() => doExport("canonical", "csv")} className="al-btn-ghost border-0 text-xs"><Download className="w-3.5 h-3.5" /> CSV</button>
            <button data-testid="export-gbp-button" onClick={() => doExport("gbp", "csv")} className="al-btn-ghost border-0 text-xs">GBP CSV</button>
            <button data-testid="export-excel-button" onClick={() => doExport("canonical", "excel")} className="al-btn-ghost border-0 text-xs">Excel</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Filters */}
        {showFilters && (
          <aside className="lg:col-span-3 al-card p-4 h-fit lg:sticky lg:top-36 space-y-4" data-testid="filter-panel">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-amber-400" /> Filters</h3>
              <button data-testid="reset-filters-button" onClick={reset} className="text-[11px] text-slate-500 hover:text-amber-400 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>
            </div>

            <div>
              <label className="al-label">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
                <input data-testid="filter-search-input" className="al-input pl-9" placeholder="Title, artist, sale…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="al-label">Artist</label>
              <input data-testid="filter-artist-input" className="al-input" placeholder="e.g. Picasso" value={filters.artist} onChange={(e) => setFilters({ ...filters, artist: e.target.value })} />
            </div>

            <div>
              <label className="al-label">Auction House</label>
              <div className="grid grid-cols-2 gap-1.5">
                {HOUSES.map((h) => (
                  <button key={h} data-testid={`filter-house-${h.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => toggleHouse(h)}
                    className={`text-[11px] px-2 py-1.5 rounded border transition-colors ${filters.houses.includes(h) ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-400 hover:border-slate-600"}`}>
                    {h}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="al-label">City</label>
                <select data-testid="filter-city-select" className="al-input" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}>
                  <option value="">Both</option>{CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="al-label">Status</label>
                <select data-testid="filter-status-select" className="al-input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <option value="">All</option><option>Sold</option><option>Unsold</option><option>Withdrawn</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div><label className="al-label">From</label><input data-testid="filter-start-date" type="date" className="al-input" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} /></div>
              <div><label className="al-label">To</label><input data-testid="filter-end-date" type="date" className="al-input" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} /></div>
            </div>

            <div>
              <label className="al-label">Category</label>
              <select data-testid="filter-category-select" className="al-input" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                <option value="">All fine art</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div><label className="al-label">GBP min</label><input data-testid="filter-gbp-min" type="number" className="al-input" value={filters.gbp_min} onChange={(e) => setFilters({ ...filters, gbp_min: e.target.value })} /></div>
              <div><label className="al-label">GBP max</label><input data-testid="filter-gbp-max" type="number" className="al-input" value={filters.gbp_max} onChange={(e) => setFilters({ ...filters, gbp_max: e.target.value })} /></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="al-label">Currency</label>
                <select data-testid="filter-currency-select" className="al-input" value={filters.currency} onChange={(e) => setFilters({ ...filters, currency: e.target.value })}>
                  <option value="">All</option><option>GBP</option><option>USD</option><option>EUR</option>
                </select>
              </div>
              <div>
                <label className="al-label">Basis</label>
                <select data-testid="filter-basis-select" className="al-input" value={filters.price_basis} onChange={(e) => setFilters({ ...filters, price_basis: e.target.value })}>
                  <option value="">All</option><option value="hammer_price">Hammer</option><option value="price_including_buyers_premium">Incl. premium</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="al-label">Review</label>
                <select data-testid="filter-review-select" className="al-input" value={filters.review_required} onChange={(e) => setFilters({ ...filters, review_required: e.target.value })}>
                  <option value="">All</option><option value="true">Needs review</option><option value="false">Clean</option>
                </select>
              </div>
              <div>
                <label className="al-label">High bid</label>
                <select data-testid="filter-bid-select" className="al-input" value={filters.has_bid} onChange={(e) => setFilters({ ...filters, has_bid: e.target.value })}>
                  <option value="">All</option><option value="true">With bid</option><option value="false">Without</option>
                </select>
              </div>
            </div>

            <button data-testid="apply-filters-button" onClick={apply} className="al-btn w-full">Apply filters</button>
          </aside>
        )}

        {/* Table */}
        <section className={showFilters ? "lg:col-span-9" : "lg:col-span-12"}>
          <div className="al-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/80">
              <span className="text-sm text-slate-400" data-testid="results-count">
                <span className="mono-num text-amber-400 font-semibold">{total}</span> lots {isFetching && <span className="text-slate-600">· loading…</span>}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="research-table">
                <thead>
                  <tr className="border-b border-slate-800 bg-[#0E131F]">
                    {COLS.map((c) => (
                      <th key={c.key} className="px-3 py-2.5 whitespace-nowrap">
                        <button onClick={() => ["estimate", "source"].includes(c.key) ? null : setSortKey(c.key)}
                          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-amber-400">
                          {c.label}{!["estimate", "source"].includes(c.key) && <ArrowUpDown className="w-3 h-3 opacity-50" />}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((l, i) => (
                    <tr key={l.lot_id} data-testid={`research-row-${i}`} onClick={() => setDrawer(l.lot_id)}
                      className="border-b border-slate-800/50 hover:bg-amber-500/[0.04] cursor-pointer transition-colors">
                      <td className="px-3 py-3 font-serif text-amber-400 whitespace-nowrap">{l.artist}</td>
                      <td className="px-3 py-3 text-sm text-slate-300 max-w-[180px] truncate">{l.title} <span className="text-slate-600">· {l.work_year_or_date}</span></td>
                      <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">{l.auction_house}</td>
                      <td className="px-3 py-3 text-xs text-slate-400">{l.sale_location}</td>
                      <td className="px-3 py-3 text-xs mono-num text-slate-400 whitespace-nowrap">{fmtDate(l.sale_date)}</td>
                      <td className="px-3 py-3 text-xs mono-num text-slate-500">{l.lot_number}</td>
                      <td className="px-3 py-3">
                        <span className={`al-chip ${statusTone[l.status] || ""}`}>{l.status}</span>
                        {l.review_required && <span className="ml-1 al-chip bg-violet-500/15 text-violet-300 border border-violet-500/30">R</span>}
                      </td>
                      <td className="px-3 py-3 text-xs mono-num text-slate-400 whitespace-nowrap">{fmtMoney(l.estimate_low, l.currency)}–{fmtMoney(l.estimate_high, "")}</td>
                      <td className="px-3 py-3 text-xs mono-num text-slate-300 whitespace-nowrap">{fmtMoney(l.result_price, l.currency)}</td>
                      <td className="px-3 py-3 text-[10px] mono-num text-slate-500 whitespace-nowrap">{(l.result_price_basis || "—").replace("price_including_buyers_premium", "premium").replace("hammer_price", "hammer")}</td>
                      <td className="px-3 py-3 text-sm mono-num font-semibold text-amber-400 whitespace-nowrap">{fmtGBP(l.result_price_gbp)}</td>
                      <td className="px-3 py-3 text-xs mono-num text-slate-400">{fmtMoney(l.highest_bid_if_unsold, l.currency)}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        {l.lot_url && <a href={l.lot_url} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-amber-400"><ExternalLink className="w-3.5 h-3.5" /></a>}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && !isFetching && (
                    <tr><td colSpan={COLS.length} className="px-3 py-16 text-center text-slate-500">No lots match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800/80">
              <span className="text-xs text-slate-500 mono-num">Page {page} / {pages}</span>
              <div className="flex gap-1">
                <button data-testid="prev-page-button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="al-btn-ghost px-2 py-1.5 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                <button data-testid="next-page-button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="al-btn-ghost px-2 py-1.5 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <LotDrawer lotId={drawer} open={!!drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}
